/**
 * Avatar Voice Chat Diagnostics Utility
 * Helps debug WebRTC and media stream issues
 */

export class AvatarDiagnostics {
  /**
   * Check browser compatibility for WebRTC
   */
  static checkBrowserCompatibility(): { compatible: boolean; issues: string[] } {
    const issues: string[] = [];
    
    // Check for getUserMedia
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      issues.push('getUserMedia not supported');
    }
    
    // Check for RTCPeerConnection
    if (!window.RTCPeerConnection) {
      issues.push('RTCPeerConnection not supported');
    }
    
    // Check for MediaRecorder (for voice mode)
    if (!window.MediaRecorder) {
      issues.push('MediaRecorder not supported');
    }
    
    return {
      compatible: issues.length === 0,
      issues
    };
  }
  
  /**
   * Test camera and microphone access
   */
  static async testMediaDevices(): Promise<{
    camera: boolean;
    microphone: boolean;
    errors: string[];
  }> {
    const result = {
      camera: false,
      microphone: false,
      errors: [] as string[]
    };
    
    // Test camera
    try {
      const videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
      result.camera = true;
      videoStream.getTracks().forEach(track => track.stop());
    } catch (error: any) {
      result.errors.push(`Camera error: ${error.message}`);
    }
    
    // Test microphone
    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      result.microphone = true;
      audioStream.getTracks().forEach(track => track.stop());
    } catch (error: any) {
      result.errors.push(`Microphone error: ${error.message}`);
    }
    
    return result;
  }
  
  /**
   * Analyze media stream for issues
   */
  static analyzeMediaStream(stream: MediaStream): {
    hasVideo: boolean;
    hasAudio: boolean;
    videoTracks: number;
    audioTracks: number;
    trackStates: { type: string; enabled: boolean; muted: boolean; label: string }[];
  } {
    const videoTracks = stream.getVideoTracks();
    const audioTracks = stream.getAudioTracks();
    
    const trackStates = [...videoTracks, ...audioTracks].map(track => ({
      type: track.kind,
      enabled: track.enabled,
      muted: track.muted,
      label: track.label
    }));
    
    return {
      hasVideo: videoTracks.length > 0,
      hasAudio: audioTracks.length > 0,
      videoTracks: videoTracks.length,
      audioTracks: audioTracks.length,
      trackStates
    };
  }
  
  /**
   * Check WebRTC connection state
   */
  static analyzeRTCConnection(pc: RTCPeerConnection): {
    connectionState: string;
    iceConnectionState: string;
    iceGatheringState: string;
    signalingState: string;
    localStreams: number;
    remoteStreams: number;
    senders: { type: string; active: boolean }[];
    receivers: { type: string; active: boolean }[];
  } {
    const senders = pc.getSenders().map(sender => ({
      type: sender.track?.kind || 'none',
      active: !!sender.track && sender.track.enabled
    }));
    
    const receivers = pc.getReceivers().map(receiver => ({
      type: receiver.track?.kind || 'none',
      active: !!receiver.track && receiver.track.enabled
    }));
    
    return {
      connectionState: pc.connectionState,
      iceConnectionState: pc.iceConnectionState,
      iceGatheringState: pc.iceGatheringState,
      signalingState: pc.signalingState,
      localStreams: senders.filter(s => s.active).length,
      remoteStreams: receivers.filter(r => r.active).length,
      senders,
      receivers
    };
  }
  
  /**
   * Get detailed device information
   */
  static async getMediaDevices(): Promise<{
    videoInputs: MediaDeviceInfo[];
    audioInputs: MediaDeviceInfo[];
    audioOutputs: MediaDeviceInfo[];
  }> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    
    return {
      videoInputs: devices.filter(d => d.kind === 'videoinput'),
      audioInputs: devices.filter(d => d.kind === 'audioinput'),
      audioOutputs: devices.filter(d => d.kind === 'audiooutput')
    };
  }
  
  /**
   * Run full diagnostics
   */
  static async runFullDiagnostics(
    localStream?: MediaStream | null,
    peerConnection?: RTCPeerConnection | null
  ): Promise<any> {
    console.log('=== Avatar Voice Chat Diagnostics ===');
    
    // Browser compatibility
    const compatibility = this.checkBrowserCompatibility();
    console.log('Browser Compatibility:', compatibility);
    
    // Media devices test
    const mediaTest = await this.testMediaDevices();
    console.log('Media Devices Test:', mediaTest);
    
    // Available devices
    const devices = await this.getMediaDevices();
    console.log('Available Devices:', {
      cameras: devices.videoInputs.length,
      microphones: devices.audioInputs.length,
      speakers: devices.audioOutputs.length
    });
    
    // Local stream analysis
    if (localStream) {
      const streamAnalysis = this.analyzeMediaStream(localStream);
      console.log('Local Stream Analysis:', streamAnalysis);
    } else {
      console.log('Local Stream: Not available');
    }
    
    // Peer connection analysis
    if (peerConnection) {
      const pcAnalysis = this.analyzeRTCConnection(peerConnection);
      console.log('Peer Connection Analysis:', pcAnalysis);
    } else {
      console.log('Peer Connection: Not established');
    }
    
    console.log('=== End Diagnostics ===');
    
    return {
      compatibility,
      mediaTest,
      devices,
      localStream: localStream ? this.analyzeMediaStream(localStream) : null,
      peerConnection: peerConnection ? this.analyzeRTCConnection(peerConnection) : null
    };
  }
}

// Export for use in LiveChat component
export default AvatarDiagnostics;